import * as mediaQuery from "components/MediaQuery";
import { Text, UnderlineText } from "components/atoms/text/Text";
import { COLORS } from "styles/colors";
import * as s from "../FilterStyled";
import { useCallback, useEffect, useRef, useState } from "react";
import TinyButton from "components/atoms/buttons/TinyButton";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import {
  MapFilter,
  MapFilterKeyword,
  mapFilterInitialState,
  setAlerts,
} from "redux/reducers/map/mapFilter";
import { DashboardFilterAlert } from "api/interfaces/dashboardInterface.interface";
import { useQueryClient } from "react-query";
import { Tree, TreeDataNode } from "antd";
import { TreeProps } from "antd/lib";

type Props = {
  onApply: () => void;
  onChangeFiltering: (name: string, value: boolean) => void;
  onChangeFilterName: (changeFilterName: string) => void;
};

// 두 객체 완전 동일한지 비교
function deepEqual(obj1: any, obj2: any): boolean {
  if (obj1 === obj2) return true;

  if (
    typeof obj1 !== "object" ||
    obj1 === null ||
    typeof obj2 !== "object" ||
    obj2 === null
  ) {
    return false;
  }

  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) {
    return false;
  }

  for (let key of keys1) {
    if (!keys2.includes(key) || !deepEqual(obj1[key], obj2[key])) {
      return false;
    }
  }

  return true;
}

const AlertFilter = (props: Props): JSX.Element => {
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();

  const filterKeyword: MapFilterKeyword = useAppSelector(
    (state) => state.mapFilter.keyword
  );

  const alertTypeFilter: DashboardFilterAlert = useAppSelector(
    (state) => state.mapFilter.filters.alerts
  );

  const themeMode = useAppSelector((state) => state.theme.theme);

  const [checkedKeys, setCheckedKeys] = useState<React.Key[]>([]);

  const alertTypes: TreeDataNode[] = [
    {
      key: "systemAlerts",
      title: "System Alerts",
      children: [
        { title: "Not Responding", key: "systemAlerts:notResponding" },
        { title: "Abnormal Restart", key: "systemAlerts:abnormalRestart" },
        { title: "System Overheated", key: "systemAlerts:systemOverheated" },
        { title: "Recording Failed", key: "systemAlerts:recording" },
      ],
    },
    {
      key: "diskAlerts",
      title: "Disk Alerts",
      children: [
        { title: "Hardware Failure", key: "diskAlerts:hardwareFailure" },
        { title: "Disk Overheated", key: "diskAlerts:diskOverheated" },
        { title: "BAD Sector", key: "diskAlerts:badSector" },
        { title: "Storage Status Check", key: "diskAlerts:storageStatus" },
        { title: "Storage Near Full", key: "diskAlerts:storageNearFull" },
        { title: "Re-indexing Time", key: "diskAlerts:reindexing" },
        {
          title: "Reserved Space Not Clearing",
          key: "diskAlerts:reservedSpaceNotClearing",
        },
      ],
    },
    {
      key: "videoAlerts",
      title: "Video Alerts",
      children: [{ title: "Video Loss", key: "videoAlerts:videoLoss" }],
    },
  ];

  const titleRendering = (nodeData: any) => {
    // console.log(nodeData);
    if (!nodeData.key.includes(":")) {
      return (
        <s.AlertItem>
          <Text bold fontSize={14}>
            {nodeData.title}
          </Text>
          <Text
            color={
              themeMode === "light" ? `${COLORS.BLACK}90` : `${COLORS.WHITE}60`
            }
            fontSize={12}
          >
            Advanced Settings
          </Text>
        </s.AlertItem>
      );
    } else {
      return <s.AlertSubs>{nodeData.title}</s.AlertSubs>;
    }
  };

  const onCheck: TreeProps["onCheck"] = (check, info) => {
    if (Array.isArray(check)) {
      let tempSplitArr = check.map((ck: React.Key) => {
        let tempKey = ck as string;
        return tempKey.split(":");
      });

      let tempArr = tempSplitArr.reduce(
        (acc: DashboardFilterAlert, cur: string[]) => {
          // console.log(cur[0], cur[1]);

          if (cur[1] === undefined) {
            return {
              ...acc,
              [cur[0]]: {
                ...acc[cur[0]],
                isChecked: true,
              },
            };
          } else {
            return {
              ...acc,
              [cur[0]]: {
                ...acc[cur[0]],
                [cur[1]]: true,
              },
            };
          }
        },
        {
          ...mapFilterInitialState.filters.alerts,
        }
      );

      let isFilter = check.length === 0;

      if (isFilter) {
        dispatch(setAlerts({ ...mapFilterInitialState.filters.alerts }));
      } else {
        dispatch(setAlerts(tempArr));
      }
    }
  };

  const onReset = () => {
    queryClient.invalidateQueries("searchMapFilterResult");
    dispatch(setAlerts({ ...mapFilterInitialState.filters.alerts }));
    setCheckedKeys([]);
  };

  useEffect(() => {
    setCheckedKeys([]);
  }, [filterKeyword]);

  useEffect(() => {
    let tempCheckArr: React.Key[] = [];

    Object.entries(alertTypeFilter).forEach((fil) => {
      Object.entries(fil[1])
        .filter((f) => {
          // console.log(f);
          if (f[0] === "isChecked" && f[1]) {
            tempCheckArr.push(`${fil[0]}`);
            return false;
          } else {
            return f[0] !== "isChecked" && f[1];
          }
        })
        .map((onlyKey) => {
          // console.log(onlyKey, "onlyKey");
          tempCheckArr.push(`${fil[0]}:${onlyKey[0]}`);
        });
    });

    // console.log(tempCheckArr, "tempCheckArr");
    setCheckedKeys(tempCheckArr);

    let isFilter = tempCheckArr.length === 0;
    props.onChangeFiltering("alerts", !isFilter);

    if (isFilter) {
      props.onChangeFilterName("Alert");
    } else {
      let tempFilterName: string = "Alert";

      // console.log(tempCheckArr, "tempCheckArr");
      let tempFindKey = tempCheckArr.filter((ck) => {
        let tempCk = ck as string;
        return !tempCk.includes(":");
      });

      if (tempCheckArr.length === 1 && tempFindKey.length === 0) {
        for (const alert of alertTypes) {
          if (alert.children) {
            for (const child of alert.children) {
              if (child.key === (tempCheckArr[0] as string)) {
                tempFilterName = child.title as string;
              }
            }
          }
        }

        // tempFilterName = checkedKeys[0] as string;
      } else {
        if (tempFindKey.length === 1) {
          if (
            tempCheckArr.filter((cc) => {
              let tempC = cc as string;
              return !tempC.includes(tempFindKey[0] as string);
            }).length > 0
          ) {
            tempFilterName = `${
              tempCheckArr.filter((ck) => {
                let tempC = ck as string;
                return tempC.includes(":");
              }).length
            } Alerts`;
          } else {
            const foundAlert = alertTypes.find(
              (alert) => alert.key === tempFindKey[0]
            );
            tempFilterName = foundAlert ? (foundAlert.title as string) : "";
          }
        } else {
          if (
            tempCheckArr.filter((ck) => {
              let tempC = ck as string;
              return !tempC.includes(":");
            }).length === 3
          ) {
            tempFilterName = "All Alerts";
          } else {
            tempFilterName = `${
              tempCheckArr.filter((ck) => {
                let tempC = ck as string;
                return tempC.includes(":");
              }).length
            } Alerts`;
          }
        }
      }
      props.onChangeFilterName(tempFilterName);
    }
  }, [alertTypeFilter]);

  return (
    <s.Container>
      <mediaQuery.Mobile>
        <s.Title>
          <Text fontSize={14} bold>
            Alert Type
          </Text>
        </s.Title>
      </mediaQuery.Mobile>
      <s.AlertContents>
        <Tree
          checkable
          autoExpandParent={true}
          selectable={false}
          onCheck={onCheck}
          checkedKeys={checkedKeys}
          titleRender={titleRendering}
          treeData={alertTypes}
          // treeData={initialAccountList}
        />
      </s.AlertContents>
      <s.Reset>
        <UnderlineText
          color={COLORS.LIGHTGRAY}
          fontSize={12}
          onClick={onReset}
          data-testid="dashboard-map-alert-underline-text-reset"
        >
          Clear
        </UnderlineText>
        <TinyButton
          label="Done"
          onClickBtn={props.onApply}
          data-testid="dashboard-map-alert-tiny-button-apply"
        />
      </s.Reset>
    </s.Container>
  );
};

export default AlertFilter;
